import { useContext } from "@nuxtjs/composition-api";
import { Logger } from "~/helpers/logger";
import type { UiNotification } from '~/composables/useUiNotification';
import { getBackendCredentialErrorMessage, isBackendCredentialError } from '~/helpers/backendCredentialError';

export type FetchPolicy = "cache-first" | "network-only" | "cache-only" | "no-cache" | "standby";

export type Variables = {
  [key: string]: unknown;
};

export type Error = {
  message: string;
  locations?: {
    line: number;
    column: number;
  }[];
  path?: string[];
  extensions?: Record<string, unknown>;
};

export type Request = <DATA, VARIABLES extends Variables = Variables>(
  request: string,
  variables?: VARIABLES,
  fetchPolicy?: FetchPolicy
) => Promise<{ data: DATA; errors: Error[] }>;

/**
 * Data and methods returned from the {@link useApi|useApi()} composable.
 */
export interface UseApiInterface {
  /**
   * Executes received GraphQL query with optional variables and headers and
   * returns the result.
   *
   * @example
   *
   * Execute a GraphQL query and handle its error, loading and result:
   *
   * ```ts
   * import { useApi } from '~/composables/useApi';
   *
   * const GET_CART_PRICE_QUERY = `
   *   query GET_CART_PRICE_QUERY($cartId: String!) {
   *     cart(cart_id: $cartId) {
   *       prices {
   *         subtotal_excluding_tax {
   *           value
   *         }
   *       }
   *     }
   *   }
   * `;
   *
   * export default {
   *   setup() {
   *     const { query } = useApi();
   *
   *     async function getCartPrice(cartId: string) {
   *       return await query(
   *         GET_CART_PRICE_QUERY,
   *         { cartId },
   *         { 'Accept': 'application/json' }
   *       );
   *     }
   *   }
   * };
   * ```
   */
  query: Request;

  /**
   * Executes received GraphQL Mutation with optional variables and headers and
   * returns the result.
   */
  mutate: Request;
}

/**
 * Allows executing arbitrary GraphQL queries and mutations.
 *
 * See the {@link UseApiInterface} for a list of methods and values available in this composable.
 */
// Module-level guard: prevents duplicate redirects when Apollo retry fires multiple times
let redirectingToLogin = false;

export function useApi(): UseApiInterface {
  const context = useContext();

  const isUnauthorizedError = (error: any): boolean => {
    if (!error) return false;
    const msg = (error?.message || '').toLowerCase();
    const networkMsg = (error?.networkError?.message || '').toLowerCase();
    return error?.response?.status === 401
      || error?.networkError?.statusCode === 401
      || error?.statusCode === 401
      || msg.includes('401')
      || msg.includes('not authorized')
      || msg.includes('unauthorized')
      || msg.includes('composite reader')
      || networkMsg.includes('401')
      || isBackendCredentialError(error);
  };

  const redirectToLogin = async (message: string, logPrefix: string) => {
    if (redirectingToLogin) {
      Logger.debug(`${logPrefix}/unauthorized: redirect already in progress, skipping`);
      return;
    }
    redirectingToLogin = true;
    context.app.$vsf.$magento.config.state.removeCustomerToken();
    context.app.$vsf.$magento.config.state.removeCartId();
    Logger.error(`${logPrefix}/unauthorized`, message);
    setBackendCredentialErrorMessage(message);
    try {
      // context.redirect works in both SSR (HTTP 302) and client (router push)
      await context.redirect(context.app.localePath('/login'));
    } finally {
      // Reset after a tick so the guard doesn't block future legitimate sessions
      setTimeout(() => { redirectingToLogin = false; }, 5000);
    }
  };

  const setBackendCredentialErrorMessage = (message: string) => {
    context.app.$vsf.$magento.config.state.setMessage<UiNotification>({
      id: Symbol(''),
      message,
      type: 'danger',
      icon: null,
      persist: true,
      title: null,
    });
  };

  const query: Request = async <DATA>(request, variables) => {
    const reqID = `id${Math.random().toString(16).slice(2)}`;
    Logger.debug(`customQuery/request/${reqID}`, request);
    try {
      const { data, errors } = await context.app.$vsf.$magento.api.customQuery({ query: request, queryVariables: variables });
      if (isBackendCredentialError(errors)) {
        const message = getBackendCredentialErrorMessage(errors);
        Logger.error(`customQuery/backend-credential-error/${reqID}`, message);
        setBackendCredentialErrorMessage(message);
      }

      Logger.debug(`customQuery/result/${reqID}`, { data, errors });

      return {
        data: data as DATA,
        errors: (errors || []) as Error[],
      };
    } catch (error) {
      if (isUnauthorizedError(error)) {
        const message = isBackendCredentialError(error)
          ? getBackendCredentialErrorMessage(error as any)
          : 'You are not authorized. Please log in again.';
        await redirectToLogin(message, `customQuery/${reqID}`);
        return {
          data: undefined as unknown as DATA,
          errors: [{ message }],
        };
      }

      if (!isBackendCredentialError(error)) {
        throw error;
      }

      const message = getBackendCredentialErrorMessage(error as any);
      Logger.error(`customQuery/backend-credential-error/${reqID}`, message);
      setBackendCredentialErrorMessage(message);

      return {
        data: undefined as unknown as DATA,
        errors: [{ message }],
      };
    }
  };

  const mutate: Request = async <DATA>(request, variables) => {
    const reqID = `id${Math.random().toString(16).slice(2)}`;
    Logger.debug(`customQuery/request/${reqID}`, request);
    try {
      const { data, errors } = await context.app.$vsf.$magento.api.customMutation({ mutation: request, mutationVariables: variables });
      if (isBackendCredentialError(errors)) {
        const message = getBackendCredentialErrorMessage(errors);
        Logger.error(`customMutation/backend-credential-error/${reqID}`, message);
        setBackendCredentialErrorMessage(message);
      }

      Logger.debug(`customQuery/result/${reqID}`, { data, errors });

      return {
        data: data as DATA,
        errors: (errors || []) as Error[],
      };
    } catch (error) {
      if (isUnauthorizedError(error)) {
        const message = isBackendCredentialError(error)
          ? getBackendCredentialErrorMessage(error as any)
          : 'You are not authorized. Please log in again.';
        await redirectToLogin(message, `customMutation/${reqID}`);
        return {
          data: undefined as unknown as DATA,
          errors: [{ message }],
        };
      }

      if (!isBackendCredentialError(error)) {
        throw error;
      }

      const message = getBackendCredentialErrorMessage(error as any);
      Logger.error(`customMutation/backend-credential-error/${reqID}`, message);
      setBackendCredentialErrorMessage(message);

      return {
        data: undefined as unknown as DATA,
        errors: [{ message }],
      };
    }
  };

  return { query, mutate };
}

export default useApi;
